/**
 * Sorting Utilities
 *
 * Client-side sorting logic for the DataTable component.
 */

import { ColumnDefinition, SortDirection } from '../types';

/**
 * Get value from row using column accessor
 */
export function getColumnValue<TData>(
  row: TData,
  column: ColumnDefinition<TData>
): any {
  if (typeof column.accessor === 'function') {
    return column.accessor(row);
  }
  return row[column.accessor];
}

/**
 * Compare two values for sorting
 */
function compareValues(a: any, b: any, direction: SortDirection): number {
  if (direction === null) return 0;

  // Handle null/undefined
  if (a == null && b == null) return 0;
  if (a == null) return direction === 'asc' ? 1 : -1;
  if (b == null) return direction === 'asc' ? -1 : 1;

  // Handle numbers
  if (typeof a === 'number' && typeof b === 'number') {
    return direction === 'asc' ? a - b : b - a;
  }

  // Handle dates
  if (a instanceof Date && b instanceof Date) {
    return direction === 'asc'
      ? a.getTime() - b.getTime()
      : b.getTime() - a.getTime();
  }

  // Handle strings (case-insensitive)
  const strA = String(a).toLowerCase();
  const strB = String(b).toLowerCase();

  if (strA < strB) return direction === 'asc' ? -1 : 1;
  if (strA > strB) return direction === 'asc' ? 1 : -1;
  return 0;
}

/**
 * Sort data array by column
 */
export function sortData<TData>(
  data: TData[],
  column: ColumnDefinition<TData> | null,
  direction: SortDirection
): TData[] {
  if (!column || direction === null) {
    return data;
  }

  return [...data].sort((a, b) => {
    const valueA = getColumnValue(a, column);
    const valueB = getColumnValue(b, column);
    return compareValues(valueA, valueB, direction);
  });
}

/**
 * Toggle sort direction
 */
export function toggleSortDirection(
  current: SortDirection,
  defaultDirection: Exclude<SortDirection, null> = 'asc',
): SortDirection {
  if (current === null) return defaultDirection;
  if (defaultDirection === 'desc') {
    if (current === 'desc') return 'asc';
    return null;
  }
  if (current === 'asc') return 'desc';
  return null;
}
